// Building blocks from docs/DESIGN.md → Components.
import type { ButtonHTMLAttributes, ReactNode } from 'react';

function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Surface with a 1 px border and a header row: title left, mono meta right. */
export function Panel({
  title,
  meta,
  actions,
  level = 'main',
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  /** Controls on the right of the header, instead of meta. */
  actions?: ReactNode;
  /** `main` panels have a 20 px title, `side` panels 16 px. */
  level?: 'main' | 'side';
  children?: ReactNode;
}) {
  const Heading = level === 'main' ? 'h2' : 'h3';
  return (
    <section className="min-w-0 border border-line bg-surface">
      <div
        className={cx(
          'flex flex-wrap items-baseline justify-between gap-3 border-b border-line px-4',
          level === 'main' ? 'py-3.5' : 'py-3',
        )}
      >
        <Heading className={cx('m-0 font-semibold', level === 'main' ? 'text-title' : 'text-name')}>
          {title}
        </Heading>
        {actions ??
          (meta !== undefined && <span className="font-mono text-label text-muted">{meta}</span>)}
      </div>
      {children}
    </section>
  );
}

/** Never blank: muted text where data would be. */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="m-0 px-4 py-4 text-secondary text-muted">{children}</p>;
}

/** KPI strip, then a main column and a side column that wrap to one column on a phone. */
export function PageLayout({
  kpis,
  main,
  side,
}: {
  kpis?: ReactNode;
  main: ReactNode;
  side?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5">
      {kpis && <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">{kpis}</div>}
      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[999_1_600px] flex-col gap-5">{main}</div>
        {side && <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">{side}</aside>}
      </div>
    </div>
  );
}

/** Uppercase label above a mono value, with an optional muted unit. */
export function KpiTile({
  label,
  value,
  unit,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
}) {
  return (
    <div className="min-w-0 border border-line bg-surface px-4 py-3.5 sm:flex-[1_1_200px]">
      <div className="text-label uppercase tracking-label text-muted">{label}</div>
      <div className="mt-1 font-mono text-kpi font-medium">
        {value}
        {unit && <span className="text-muted">{unit}</span>}
      </div>
    </div>
  );
}

/** Mono 11 px tag on the accent. */
export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="bg-accent px-1.5 py-0.5 font-mono text-tag font-semibold text-bg">
      {children}
    </span>
  );
}

const buttonBase =
  'inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 border bg-transparent px-4 font-mono text-compact font-semibold disabled:cursor-default disabled:opacity-50';

/** 1 px accent outline with accent text. */
export function PrimaryButton({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={cx(buttonBase, 'border-accent text-accent', className)} {...props} />;
}

/** Same shape as the primary button, in the line colour. */
export function SecondaryButton({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cx(buttonBase, 'border-line text-muted hover:text-text', className)}
      {...props}
    />
  );
}

/** Data grid: CSS grid rows, horizontally scrollable at 820 px minimum on a phone. */
export function DataGrid({
  columns,
  header,
  children,
  label,
}: {
  /** grid-template-columns */
  columns: string;
  header: string[];
  children: ReactNode;
  label: string;
}) {
  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label={label} className="min-w-[820px]">
        <div
          role="row"
          className="grid gap-x-3.5 border-b border-line px-4 py-2.5 text-label uppercase tracking-label text-muted"
          style={{ gridTemplateColumns: columns }}
        >
          {header.map((h, i) => (
            <span role="columnheader" key={i}>
              {h}
            </span>
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}

export function DataRow({ columns, children }: { columns: string; children: ReactNode }) {
  return (
    <div
      role="row"
      className="grid items-center gap-x-3.5 border-b border-line-soft px-4 py-3 last:border-b-0"
      style={{ gridTemplateColumns: columns }}
    >
      {children}
    </div>
  );
}

/** A labelled checkbox with a 44 px touch target. */
export function Toggle({
  label,
  checked,
  disabled,
  hint,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  hint?: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 has-disabled:cursor-default">
      <input
        type="checkbox"
        className="size-5 shrink-0 cursor-pointer disabled:cursor-default"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-compact">{label}</span>
        {hint && <span className="text-label text-muted">{hint}</span>}
      </span>
    </label>
  );
}

/** A filter chip: a toggle button with an optional count, 44 px tall. */
export function Chip({
  pressed,
  count,
  children,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & { pressed: boolean; count?: number }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cx(
        'inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 border px-3 text-compact',
        pressed
          ? 'border-accent bg-accent-row text-accent'
          : 'border-line text-muted hover:text-text',
      )}
      {...props}
    >
      {children}
      {count !== undefined && <span className="font-mono text-label">{count}</span>}
    </button>
  );
}
